// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { Icon, LazyImage } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";

interface OwnProps {
    title: string;
    linkTitle: string;
    linkUrl: LinkFieldValue;
    extendedStyles?: FeaturedCategoryCardStyles;
    categories?: Array<null | {
        name: string;
        shortDescription: string;
        imageSrc: string;
        path: string;
        id: string;
    }>;
}

type Props = OwnProps;

export interface FeaturedCategoryCardStyles {
    container?: InjectableCss;
    box?: GridContainerProps;
    cardContainer?: GridItemProps;
    cardContainerLink?: LinkPresentationProps;
    link?: LinkPresentationProps;
    subtitle?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    titleWrapper?: InjectableCss;
    titleLeftSection?: InjectableCss;
    starIcon?: IconPresentationProps;
}

export const featuredCategoryCardStyle: FeaturedCategoryCardStyles = {};

const FeaturedCategoryCard: React.FC<Props> = ({ title, linkTitle, linkUrl, extendedStyles, categories }) => {
    const [styles] = React.useState(() => mergeWithCss(featuredCategoryCardStyle, extendedStyles));

    const link = useGetLink(linkUrl);

    return (
        <>
            <StyledWrapper {...styles.titleWrapper}>
                <StyledWrapper {...styles.titleLeftSection}>
                    {title && <Typography {...styles.title}>{title}</Typography>}
                    {linkTitle && (
                        <Link href={link.url} {...styles.link}>
                            {linkTitle}
                        </Link>
                    )}
                </StyledWrapper>

                <Icon {...styles.starIcon} />
            </StyledWrapper>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.box}>
                    {categories?.map(item => {
                        if (!item) {
                            return null;
                        }
                        return (
                            <StyledWrapper {...styles.cardContainer} key={item.id}>
                                <Link {...styles.cardContainerLink} href={item.path}>
                                    <LazyImage
                                        src={item.imageSrc}
                                        altText={item.shortDescription}
                                        width="auto"
                                        height="132px"
                                        imgProps={{ "aria-label": `Go Category ${item.shortDescription}` }}
                                    />
                                    <Typography {...styles.subtitle}>{item.shortDescription}</Typography>
                                </Link>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

export default FeaturedCategoryCard;
